import { useEffect, useState } from 'react';

/**
 * Detects whether the mobile on-screen keyboard is currently visible.
 *
 * Uses `window.visualViewport.height` vs `window.innerHeight` to determine
 * keyboard presence. When the soft keyboard opens, the visual viewport shrinks
 * but the layout viewport (`innerHeight`) stays the same — the ratio drop
 * signals an open keyboard.
 *
 * Returns `false` on desktop (when `enabled` is false).
 */
export function useIsKeyboardOpen({ enabled }: { enabled: boolean }): boolean {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const vv = window.visualViewport;
    if (!vv) return;

    function check() {
      const ratio = (vv?.height ?? window.innerHeight) / window.innerHeight;
      setIsKeyboardOpen(ratio < 0.75);
    }

    check();
    vv.addEventListener('resize', check);

    return () => {
      vv.removeEventListener('resize', check);
      setIsKeyboardOpen(false);
    };
  }, [enabled]);

  return isKeyboardOpen;
}
